import { FC, useRef, useState } from "react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { RulesForm, RulesFormRef } from "src/components/scenario/RulesForm";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useCreateError } from "src/errors";
import { getFullScenarioQuery } from "src/graphql/anti-fraud/queries/scenarios/GetFullScenarioQuery.ts";
import { useUpdateScenarioDraftVersionMutation } from "src/graphql/anti-fraud/queries/scenarios/UpdateScenarioDraftVersionMutation.ts";
import { useT } from "src/internationalization";
import {
  EditScenarioVersionForm,
  EditScenarioVersionFormRef,
} from "src/pages/ScenarioDetails/EditScenarioDraftVersionModal/EditScenarioVersionForm.tsx";
import { EditScenarioDraftVersionData } from "src/pages/ScenarioDetails/EditScenarioDraftVersionModal/types.ts";
import { isDefinedScenarioRule, ScenarioVersionWithRules } from "src/types/scenario.ts";
import { cn } from "src/utils/string.utils";

export type EditScenarioDraftVersionModalProps = {
  scenarioId: string;
  onClose: () => void;
  draftVersion: ScenarioVersionWithRules;
};

export const EditScenarioDraftVersionModal: FC<EditScenarioDraftVersionModalProps> = ({
  scenarioId,
  onClose,
  draftVersion,
}) => {
  const t = useT();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isRulesDirty, setIsRulesDirty] = useState(false);
  const { showSuccessToast } = useToast();
  const createError = useCreateError();

  const dataRef = useRef<EditScenarioDraftVersionData>({
    rules: [],
    name: "",
    threshold: 0,
    description: "",
  });

  const [
    updateScenarioDraftVersion,
    { loading: updateScenarioDraftVersionLoading, error: updateScenarioDraftVersionError },
  ] = useUpdateScenarioDraftVersionMutation({
    refetchQueries: [{ query: getFullScenarioQuery, variables: { id: scenarioId } }],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async () => {
    try {
      if (!draftVersion) return;

      await updateScenarioDraftVersion({
        variables: {
          id: draftVersion.id,
          options: {
            name: dataRef.current.name,
            description: dataRef.current.description,
            rules: dataRef.current.rules.map((r) => ({
              ruleId: r.id,
              score: r.score,
            })),
            threshold: dataRef.current.threshold,
          },
        },
      });

      onClose();
      showSuccessToast(t("edit.scenario.version.confirmation.modal.success.toast.title"));
    } catch {
      // Ignore error
    }
  };

  const submitVersionForm = () => {
    editScenarioVersionFormRef.current?.submit(
      (data) => {
        dataRef.current = { ...dataRef.current, ...data };
        stepsControl.setStepError("version", false);
        stepsControl.goNextStep();
      },
      () => {
        stepsControl.setStepError("version", true);
      }
    )();
  };

  const submitRulesForm = () => {
    scenarioVersionRulesFormRef.current?.submit(
      async (formData) => {
        dataRef.current.rules = formData;
        stepsControl.setStepError("rules", false);
        handleSubmit();
      },
      () => {
        stepsControl.setStepError("rules", true);
      }
    )();
  };

  const { control: stepsControl, modalProps } = useProgressStepModal([
    {
      type: "version",
      title: t("edit.scenario.version.modal.version.tab.name"),
      submit: submitVersionForm,
      focus: "#versionName",
    },
    {
      type: "rules",
      title: t("rules.title"),
      submit: submitRulesForm,
      focus: "#searchBlacklistable",
    },
  ]);

  const editScenarioVersionFormRef = useRef<EditScenarioVersionFormRef | null>(null);
  const scenarioVersionRulesFormRef = useRef<RulesFormRef | null>(null);

  const error = updateScenarioDraftVersionError
    ? createError("SERVER-ERROR", updateScenarioDraftVersionError)
    : null;

  return (
    <ProgressStepModal
      kind="horizontal"
      onClose={onClose}
      title={t("edit.scenario.version.modal.title")}
      subtitle={draftVersion.name}
      loading={updateScenarioDraftVersionLoading}
      error={mapToModalError(error)}
      noPaddingX
      initialFocus="#versionName"
      confirmButtonText={t("general.actions.confirm")}
      confirmButtonDisabled={!isFormDirty && !isRulesDirty}
      {...modalProps}
    >
      <div
        className={cn("flex flex-col px-5 h-full", {
          hidden: !stepsControl.isStep("version"),
        })}
      >
        <EditScenarioVersionForm
          ref={editScenarioVersionFormRef}
          draftVersion={draftVersion}
          onValidChange={(isValid) => stepsControl.setStepError("version", !isValid)}
          onDirtyChange={setIsFormDirty}
        />
      </div>
      <div
        className={cn("flex flex-col h-full overflow-hidden", {
          hidden: !stepsControl.isStep("rules"),
        })}
      >
        <RulesForm
          caseTypeCode={draftVersion.scenario?.caseTypeCode}
          title={t("edit.scenario.version.modal.rules.tab.title")}
          subtitle={t("edit.scenario.version.modal.rules.tab.subtitle")}
          ref={scenarioVersionRulesFormRef}
          onValidChange={(isValid) => stepsControl.setStepError("rules", !isValid)}
          onDirtyChange={setIsRulesDirty}
          defaultScenarioRules={draftVersion.rules.filter(isDefinedScenarioRule)}
        />
      </div>
    </ProgressStepModal>
  );
};
